import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import type { AccountWORiskAllocation } from "../../../../api/src/accounts/accounts.service";
import type { AccountCashSetting } from "../../../../api/src/cash-settings/lib";
import { feeLocationDisplay } from "../../billing/lib";
import IndeterminateCheckbox from "../../components/IndeterminateCheckbox";
import { Table, useTable } from "../../components/Table/Table";
import {
  ColumnHeaderWithHelp,
  cashSplitPercentHelpMessage,
  cashSplitSymbolHelpMessage,
  displayCashTarget,
  displayMinMaxCashTarget,
  getCashTargetPercentage,
} from "../../cash/lib";
import { displayAccountName, naLabel } from "../../lib/display";
import { formatCurrency, formatPercent } from "../../lib/numbers";

export type HouseholdCashBillingRow = AccountWORiskAllocation &
  Partial<AccountCashSetting> & {
    cashSettingName?: string;
    feeStructureName?: string;
    isFeeStructureInherited: boolean;
    feeLocationAccountName?: string;
  };

const columnHelper = createColumnHelper<HouseholdCashBillingRow>();

export const useHouseholdCashBillingTable = ({
  accounts,
  enableSelection = false,
}: {
  accounts: HouseholdCashBillingRow[];
  enableSelection?: boolean;
}) => {
  const columns = useMemo(
    () => [
      ...(enableSelection
        ? [
            columnHelper.display({
              id: "select",
              header: ({ table }) => (
                <IndeterminateCheckbox
                  {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomeRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                  }}
                />
              ),
              cell: ({ row }) => (
                <div className="px-1">
                  <IndeterminateCheckbox
                    {...{
                      checked: row.getIsSelected(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </div>
              ),
            }),
          ]
        : []),
      columnHelper.accessor(
        (row) => displayAccountName(row.displayName, row.displayNumber),
        {
          id: "name",
          cell: (info) => (
            <Link to={info.row.original.id.toString()}>{info.getValue()}</Link>
          ),
          header: () => "Account",
          minSize: 225,
        },
      ),
      columnHelper.accessor((row) => row.cashSettingName ?? "", {
        id: "cashSettingName",
        cell: (info) => {
          const cashSettingId = info.row.original.cashSettingId;

          return cashSettingId == null ? (
            ""
          ) : (
            <Link to={`/cash/settings/${cashSettingId}`}>
              {info.getValue()}
            </Link>
          );
        },
        header: () => "Cash Setting",
        minSize: 225,
      }),
      columnHelper.accessor((row) => row.accountBalance ?? 0, {
        id: "accountBalance",
        cell: (info) => formatCurrency(info.getValue()),
        header: () => "Total Assets",
        enableColumnFilter: false,
      }),
      columnHelper.accessor(
        (row) =>
          row.accountBalance === 0
            ? 0
            : (row.cashBalance ?? 0) /
              (row.accountBalance ?? row.cashBalance ?? 1),
        {
          id: "cashWeight",
          cell: (info) => formatPercent(info.getValue(), 2),
          header: () => "Cash %",
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor(
        (row) =>
          getCashTargetPercentage(
            row.accountBalance ?? 0,
            "percent",
            row.cashTargetMinAbsolute,
            row.cashTargetMinPercent,
          ),
        {
          id: "cashTargetMin",
          cell: (info) =>
            displayMinMaxCashTarget(
              info.row.original.accountBalance ?? 0,
              info.row.original.cashTargetMinCondition ?? "and",
              info.row.original.cashTargetMinAbsolute,
              info.row.original.cashTargetMinPercent,
            ),
          header: () => "Min Cash Trigger",
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor(
        (row) =>
          getCashTargetPercentage(
            row.accountBalance ?? 0,
            row.cashTargetType ?? "absolute",
            row.cashTargetAbsolute,
            row.cashTargetPercent,
          ),
        {
          id: "cashTarget",
          cell: (info) =>
            displayCashTarget(
              info.row.original.accountBalance ?? 0,
              info.row.original.cashTargetType ?? "absolute",
              info.row.original.cashTargetAbsolute,
              info.row.original.cashTargetPercent,
            ),
          header: () => "Cash Target",
          enableColumnFilter: false,
        },
      ),
      columnHelper.accessor(
        (row) =>
          getCashTargetPercentage(
            row.accountBalance ?? 0,
            "percent",
            row.cashTargetMaxAbsolute,
            row.cashTargetMaxPercent,
          ),
        {
          id: "cashTargetMax",
          cell: (info) =>
            displayMinMaxCashTarget(
              info.row.original.accountBalance ?? 0,
              info.row.original.cashTargetMaxCondition ?? "and",
              info.row.original.cashTargetMaxAbsolute,
              info.row.original.cashTargetMaxPercent,
            ),
          header: () => "Max Cash Trigger",
          enableColumnFilter: false,
        },
      ),
      // The account's own split, not the assigned cash setting's. Spec
      // doc-cash-split-01a05d5f47df200b §4.1: `account.cash_split_*` is what a
      // rebalance reads, and the `cash_setting` copy "exists so the account form
      // can offer a starting value, and for no other purpose". So unlike the
      // target columns above, these must never resolve through the cash
      // setting. HouseholdCashBilling.tsx:385-389 spreads the account's own
      // AccountCashSetting onto the row, which is where both values come from.
      //
      // `cash_split_percent` is stored 0-100 — `decimal(5, 2)`, the same shape
      // as `cash_target_percent` — while formatPercent applies `style:
      // "percent"` and multiplies by 100 (../../lib/numbers.tsx:54-64), so
      // divide first. That is what getCashTargetPercentage does for every
      // percentage above (../../cash/lib.tsx:27-38).
      columnHelper.accessor((row) => row.cashSplitPercent ?? null, {
        id: "cashSplitPercent",
        cell: (info) => {
          const val = info.getValue();
          return val === null ? naLabel : formatPercent(val / 100, 2);
        },
        header: () => (
          <ColumnHeaderWithHelp
            label="Cash Split % of Target"
            tooltip={cashSplitPercentHelpMessage}
          />
        ),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.cashSplitSymbol ?? null, {
        id: "cashSplitSymbol",
        cell: (info) => info.getValue() ?? naLabel,
        header: () => (
          <ColumnHeaderWithHelp
            label="Cash Split"
            tooltip={cashSplitSymbolHelpMessage}
          />
        ),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.feeStructureName ?? "", {
        id: "feeStructure",
        cell: (info) => (
          <Link
            to={`/billing/fee-structure/${info.row.original.feeStructureId}`}
          >
            {info.row.original.isFeeStructureInherited ? (
              <em>{info.getValue()}</em>
            ) : (
              info.getValue()
            )}
          </Link>
        ),
        header: () => "Fee Structure",
        minSize: 225,
      }),
      columnHelper.accessor(
        (row) =>
          feeLocationDisplay(
            row.feeLocation ?? "S",
            row.feeLocationAccountName,
          ),
        {
          id: "feeLocation",
          cell: (info) =>
            info.row.original.feeLocation !== "A" ? (
              info.getValue()
            ) : (
              <Link
                to={`/clients/accounts/${info.row.original.feeLocationAccountId}`}
              >
                {info.getValue()}
              </Link>
            ),
          header: () => "Fee Location",
          enableColumnFilter: false,
        },
      ),
    ],
    [enableSelection],
  );

  const getRowId = useCallback(
    (row: HouseholdCashBillingRow) => row.id.toString(),
    [],
  );

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: accounts,
    getRowId,
    enableRowSelection: enableSelection,
    autoResetExpanded: false,
    autoResetAll: false,
  });

  // Reset pagination to first page when filters change
  useEffect(() => {
    table.setPageIndex(0);
  }, [table.getState().columnFilters, table]);

  return { table, rowSelection, setRowSelection };
};

const HouseholdCashBillingTable = ({
  accounts,
}: {
  accounts: HouseholdCashBillingRow[];
}) => {
  const { table } = useHouseholdCashBillingTable({
    accounts,
    enableSelection: false,
  });

  return <Table table={table} />;
};

export default HouseholdCashBillingTable;
